/**
 * V10 Phase C — Episode attach 모달.
 *
 * 신규 episode + edge 링크 1건. "다른 edge 에도 연결하기" 섹션으로 M:N 동시 attach 가능.
 */
import { useState } from "react"
import type { AttachEpisodeInput } from "../../../api/admin-client.ts"

type Props = {
	readonly defaultEdge: { readonly fromId: string; readonly toId: string; readonly relation: string }
	readonly onClose: () => void
	readonly onSubmit: (input: AttachEpisodeInput) => Promise<void>
}

export function EdgeAttachEpisodeModal(props: Props) {
	const [content, setContent] = useState("")
	const [source, setSource] = useState("message")
	const [occurredAt, setOccurredAt] = useState(() => new Date().toISOString().slice(0, 16))
	const [extraEdges, setExtraEdges] = useState<Array<{ fromId: string; toId: string; relation: string }>>([])
	const [extraInput, setExtraInput] = useState({ fromId: "", toId: "", relation: "" })
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | undefined>()

	async function onSubmit() {
		if (!content.trim()) {
			setError("content 는 필수입니다")
			return
		}
		setSubmitting(true)
		setError(undefined)
		try {
			await props.onSubmit({
				toEdges: [props.defaultEdge, ...extraEdges],
				content: content.trim(),
				source: source.trim() || "message",
				occurredAt: new Date(occurredAt).toISOString()
			})
			props.onClose()
		} catch (e) {
			setError((e as Error).message)
			setSubmitting(false)
		}
	}

	return (
		// biome-ignore lint/a11y/useKeyWithClickEvents: 백드롭 클릭만 닫힘 — Escape 키보드 대응은 상위 훅에서 처리 권장
		<div
			className="modal-backdrop"
			role="dialog"
			aria-modal="true"
			aria-labelledby="attach-title"
			onClick={(e) => {
				if (e.target === e.currentTarget) props.onClose()
			}}
			style={{
				position: "fixed",
				inset: 0,
				background: "rgba(0,0,0,0.4)",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				zIndex: 1000
			}}
		>
			<div
				style={{
					width: "min(560px, 92vw)",
					background: "var(--pk-bg-elevated)",
					border: "1px solid var(--pk-border)",
					borderRadius: "var(--pk-radius-lg)",
					padding: "var(--pk-space-6)",
					boxShadow: "var(--pk-shadow-lg)"
				}}
			>
				<h3 id="attach-title" style={{ margin: 0, marginBottom: "var(--pk-space-4)" }}>
					새 사건 첨부
				</h3>

				<div className="field" style={{ marginBottom: "var(--pk-space-3)" }}>
					<label className="field-label" htmlFor="att-content">
						내용 (근거)
					</label>
					<textarea
						id="att-content"
						className="input"
						rows={3}
						value={content}
						onChange={(e) => setContent(e.target.value)}
						placeholder="왜 이 관계인가를 뒷받침하는 사건/메시지/노트"
						style={{ width: "100%" }}
					/>
				</div>

				<div style={{ display: "flex", gap: "var(--pk-space-3)", marginBottom: "var(--pk-space-3)" }}>
					<div className="field" style={{ flex: 1 }}>
						<label className="field-label" htmlFor="att-source">
							출처
						</label>
						<input
							id="att-source"
							className="input"
							value={source}
							onChange={(e) => setSource(e.target.value)}
							style={{ width: "100%" }}
						/>
					</div>
					<div className="field" style={{ flex: 1 }}>
						<label className="field-label" htmlFor="att-time">
							발생 시각
						</label>
						<input
							id="att-time"
							className="input"
							type="datetime-local"
							value={occurredAt}
							onChange={(e) => setOccurredAt(e.target.value)}
							lang="ko-KR"
							style={{ width: "100%" }}
						/>
					</div>
				</div>

				<div
					style={{
						marginBottom: "var(--pk-space-3)",
						padding: "var(--pk-space-3)",
						background: "var(--pk-bg-surface)",
						borderRadius: "var(--pk-radius-md)"
					}}
				>
					<div
						style={{
							fontSize: "0.8rem",
							color: "var(--pk-text-muted)",
							marginBottom: "var(--pk-space-2)"
						}}
					>
						다른 edge 에도 연결하기 (M:N · 같은 사건을 여러 관계에 동시 attach)
					</div>
					<div style={{ display: "flex", gap: "var(--pk-space-1)", marginBottom: "var(--pk-space-2)" }}>
						<input
							className="input"
							placeholder="fromId"
							value={extraInput.fromId}
							onChange={(e) => setExtraInput({ ...extraInput, fromId: e.target.value })}
							style={{ flex: 1, fontSize: "0.8rem" }}
						/>
						<input
							className="input"
							placeholder="relation"
							value={extraInput.relation}
							onChange={(e) => setExtraInput({ ...extraInput, relation: e.target.value })}
							style={{ width: 120, fontSize: "0.8rem" }}
						/>
						<input
							className="input"
							placeholder="toId"
							value={extraInput.toId}
							onChange={(e) => setExtraInput({ ...extraInput, toId: e.target.value })}
							style={{ flex: 1, fontSize: "0.8rem" }}
						/>
						<button
							type="button"
							className="btn btn-ghost btn-sm"
							onClick={() => {
								if (extraInput.fromId && extraInput.toId && extraInput.relation) {
									setExtraEdges([...extraEdges, { ...extraInput }])
									setExtraInput({ fromId: "", toId: "", relation: "" })
								}
							}}
						>
							추가
						</button>
					</div>
					{extraEdges.length > 0 && (
						<div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
							{extraEdges.map((e, i) => (
								<div
									// biome-ignore lint/suspicious/noArrayIndexKey: 동일 (from|relation|to) 조합의 중복 입력을 허용하므로 index 가 복합키 보조로 필요
									key={`${e.fromId}|${e.relation}|${e.toId}-${i}`}
									style={{
										fontSize: "0.75rem",
										fontFamily: "var(--pk-font-mono)",
										color: "var(--pk-text-muted)",
										display: "flex",
										gap: 4
									}}
								>
									<span style={{ flex: 1 }}>
										{e.fromId} ─{e.relation}→ {e.toId}
									</span>
									<button
										type="button"
										onClick={() => setExtraEdges(extraEdges.filter((_, j) => j !== i))}
										style={{
											background: "transparent",
											border: "none",
											cursor: "pointer",
											color: "var(--pk-danger)"
										}}
										aria-label="제거"
									>
										✕
									</button>
								</div>
							))}
						</div>
					)}
				</div>

				{error && (
					<div className="alert alert-danger" style={{ marginBottom: "var(--pk-space-3)" }}>
						{error}
					</div>
				)}

				<div style={{ display: "flex", gap: "var(--pk-space-2)", justifyContent: "flex-end" }}>
					<button type="button" className="btn btn-ghost" onClick={props.onClose} disabled={submitting}>
						취소
					</button>
					<button
						type="button"
						className="btn btn-primary"
						onClick={onSubmit}
						disabled={submitting || !content.trim()}
					>
						{submitting ? "첨부 중…" : "첨부"}
					</button>
				</div>
			</div>
		</div>
	)
}
